import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { certificationService, CertificationInterest } from '@/lib/api/services/certification.service';

export function useCertificationInterest() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const queryKey = ['certification-interest'];

  const {
    data: interest,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => certificationService.getMyInterest(),
    enabled: !!user?.id,
  });

  const updateInterestMutation = useMutation({
    mutationFn: (data: {
      is_interested: boolean;
      timezone?: string | null;
      preferred_schedule?: string | null;
      note?: string | null;
    }) => certificationService.upsertMyInterest(data),
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CertificationInterest | null>(queryKey);
      queryClient.setQueryData(queryKey, {
        ...previous,
        ...newData,
        updated_at: new Date().toISOString(),
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error('Failed to update certification interest');
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      if (data.is_interested) {
        toast.success('You\'re now open to certification cohorts!');
      } else {
        toast.success('Certification interest updated');
      }
    },
  });

  return {
    interest,
    isInterested: interest?.is_interested ?? false,
    isLoading,
    error,
    updateInterest: updateInterestMutation.mutateAsync,
    isUpdating: updateInterestMutation.isPending,
  };
}
